/**
 * Works out WHERE a snippet the assistant produced should land in the editor, without
 * touching the editor. The rule that matters: an unselected caret sitting in the middle
 * of a line never splices the snippet into that line — the code goes on its own lines,
 * indented like the line it follows.
 */

/** A position in Monaco's 1-based line/column space. */
export interface ICodePosition {
  lineNumber: number;
  column: number;
}

/** A range in Monaco's 1-based line/column space. */
export interface ICodeRange {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
}

export interface ICodeInsertionInput {
  /** Every line of the model, in order. */
  lines: string[];
  /** Where the caret was when the snippet was applied. */
  position: ICodePosition;
  /** The user's selection, or null when nothing is selected. */
  selection: ICodeRange | null;
  /** The code the assistant produced, fences already stripped. */
  snippet: string;
  /** The model's line separator. */
  eol: string;
}

/** How much of the document the plan is allowed to touch. */
export type ECodeInsertionMode = "at-cursor" | "replace-all";

export interface IPlannedCodeInsertion {
  /** The single edit to run when the user confirms. */
  range: ICodeRange;
  text: string;
  /** Lines the snippet adds, for the confirmation bar's wording. */
  addedLineCount: number;
  /** The preview is drawn under this line; 0 means above the first line. */
  previewAfterLineNumber: number;
  /** Lines the edit overwrites — the ones the preview strikes through. */
  replacedRange: ICodeRange | null;
  mode: ECodeInsertionMode;
}

const LEADING_WHITESPACE = /^[ \t]*/;

const leadingWhitespace = (line: string): string => LEADING_WHITESPACE.exec(line)?.[0] ?? "";

const isBlank = (line: string): boolean => line.trim() === "";

export const isEmptyRange = (range: ICodeRange): boolean =>
  range.startLineNumber === range.endLineNumber && range.startColumn === range.endColumn;

/** Splits the snippet into lines, drops the blank ones at either end and removes its common indent. */
const normalizeSnippet = (snippet: string): string[] => {
  const rawLines: string[] = snippet.replace(/\r\n?/g, "\n").split("\n");

  let start: number = 0;
  let end: number = rawLines.length;
  while (start < end && isBlank(rawLines[start])) start += 1;
  while (end > start && isBlank(rawLines[end - 1])) end -= 1;
  const trimmed: string[] = rawLines.slice(start, end);
  if (trimmed.length === 0) return [];

  const indents: number[] = trimmed
    .filter((line: string): boolean => !isBlank(line))
    .map((line: string): number => leadingWhitespace(line).length);
  const commonIndent: number = indents.length > 0 ? Math.min(...indents) : 0;

  return trimmed.map((line: string): string => (isBlank(line) ? "" : line.slice(commonIndent)));
};

/** Re-indents every line but the first, which inherits whatever precedes it in the document. */
const indentSnippet = (snippetLines: string[], indent: string, eol: string, indentFirstLine: boolean): string =>
  snippetLines
    .map((line: string, index: number): string => {
      if (isBlank(line)) return "";
      if (index === 0 && !indentFirstLine) return line;
      return indent + line;
    })
    .join(eol);

const isDocumentBlank = (lines: string[]): boolean => lines.every(isBlank);

const fullRange = (lines: string[]): ICodeRange => ({
  startLineNumber: 1,
  startColumn: 1,
  endLineNumber: lines.length,
  endColumn: (lines[lines.length - 1]?.length ?? 0) + 1
});

/** The indent to give inserted lines: the anchor line's own, or the closest non-blank line above it. */
const indentAround = (lines: string[], lineNumber: number): string => {
  for (let index: number = lineNumber - 1; index >= 0; index -= 1) {
    const line: string = lines[index] ?? "";
    if (!isBlank(line)) return leadingWhitespace(line);
  }
  return "";
};

const clampLineNumber = (lineNumber: number, lines: string[]): number =>
  Math.min(Math.max(lineNumber, 1), Math.max(lines.length, 1));

/**
 * Plans the edit for a snippet the user asked to apply. Returns null when there is nothing
 * to write (the assistant's message carried no code).
 */
export const planCodeInsertion = (
  input: ICodeInsertionInput,
  mode: ECodeInsertionMode = "at-cursor"
): IPlannedCodeInsertion | null => {
  const { lines, eol, selection, snippet } = input;
  const snippetLines: string[] = normalizeSnippet(snippet);
  if (snippetLines.length === 0) return null;

  const addedLineCount: number = snippetLines.length;

  // An empty document has nothing to insert next to, so it is overwritten whatever the
  // mode says — but the bar must not offer to undo a "replace" that discarded nothing.
  const documentIsBlank: boolean = isDocumentBlank(lines);
  if (mode === "replace-all" || documentIsBlank) {
    const range: ICodeRange = fullRange(lines);
    return {
      range,
      text: snippetLines.join(eol),
      addedLineCount,
      previewAfterLineNumber: 0,
      replacedRange: documentIsBlank ? null : range,
      mode: documentIsBlank ? "at-cursor" : "replace-all"
    };
  }

  // An explicit selection is an explicit instruction: overwrite exactly what is selected.
  if (selection && !isEmptyRange(selection)) {
    const anchorLine: string = lines[selection.startLineNumber - 1] ?? "";
    const indent: string = leadingWhitespace(anchorLine);
    // Only re-indent the first line when the selection starts the line; otherwise it
    // continues text that is already there.
    const startsLine: boolean = selection.startColumn <= indent.length + 1;
    return {
      range: selection,
      text: indentSnippet(snippetLines, indent, eol, startsLine),
      addedLineCount,
      previewAfterLineNumber: selection.endLineNumber,
      replacedRange: selection,
      mode: "at-cursor"
    };
  }

  const caretLine: number = clampLineNumber(input.position.lineNumber, lines);
  const caretLineText: string = lines[caretLine - 1] ?? "";

  // A blank line is a slot the user opened for the code: fill it in place.
  if (isBlank(caretLineText)) {
    const indent: string = caretLineText.length > 0 ? caretLineText : indentAround(lines, caretLine);
    return {
      range: {
        startLineNumber: caretLine,
        startColumn: 1,
        endLineNumber: caretLine,
        endColumn: caretLineText.length + 1
      },
      text: indentSnippet(snippetLines, indent, eol, true),
      addedLineCount,
      previewAfterLineNumber: caretLine - 1,
      replacedRange: null,
      mode: "at-cursor"
    };
  }

  // The line has code on it. Never split it — start a new line underneath instead.
  const endColumn: number = caretLineText.length + 1;
  const indent: string = leadingWhitespace(caretLineText);
  return {
    range: {
      startLineNumber: caretLine,
      startColumn: endColumn,
      endLineNumber: caretLine,
      endColumn
    },
    text: eol + indentSnippet(snippetLines, indent, eol, true),
    addedLineCount,
    previewAfterLineNumber: caretLine,
    replacedRange: null,
    mode: "at-cursor"
  };
};
